import { useState, useEffect, useCallback } from "react";
import {
  Stack,
  Title,
  Group,
  SimpleGrid,
  Paper,
  Image,
  Text,
  Badge,
  Loader,
  Center,
  Button,
  Pagination,
  SegmentedControl,
} from "@mantine/core";
import { IconArrowLeft, IconCheck, IconX } from "@tabler/icons-react";
import { useParams, useNavigate } from "react-router-dom";
import { notifications } from "@mantine/notifications";
import { api } from "@/lib/api";

export default function AuditDetect() {
  const { id, name } = useParams<{ id: string; name: string }>();
  const navigate = useNavigate();
  const [images, setImages] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [statusFilter, setStatusFilter] = useState("new");

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    try {
      const res = await api.audit.detail(id, {
        page,
        limit: 30,
        name,
        auditStatus: statusFilter || undefined,
      });
      setImages(res.data);
      setTotalPages(res.pagination.totalPages);
    } catch {
      // ignore
    } finally {
      setLoading(false);
    }
  }, [id, name, page, statusFilter]);

  useEffect(() => {
    load();
  }, [load]);

  const handleReview = async (imageId: string, status: string) => {
    try {
      await api.audit.review(imageId, status);
      setImages((prev) => prev.filter((img) => img._id !== imageId));
      notifications.show({
        message: status === "success" ? "Зөв" : "Буруу",
        color: status === "success" ? "green" : "red",
      });
    } catch (err: any) {
      notifications.show({ message: err.message, color: "red" });
    }
  };

  return (
    <Stack gap="lg">
      <Group>
        <Button
          variant="subtle"
          leftSection={<IconArrowLeft size={16} />}
          onClick={() => navigate(`/audit/${id}`)}
        >
          Буцах
        </Button>
        <Title order={3}>
          Аудит: <Badge size="lg">{name}</Badge>
        </Title>
      </Group>

      <SegmentedControl
        data={[
          { label: "Шинэ", value: "new" },
          { label: "Зөв", value: "success" },
          { label: "Буруу", value: "failed" },
        ]}
        value={statusFilter}
        onChange={(val) => {
          setStatusFilter(val);
          setPage(1);
        }}
      />

      {loading ? (
        <Center h={300}>
          <Loader />
        </Center>
      ) : (
        <>
          <SimpleGrid cols={{ base: 3, sm: 4, md: 6, lg: 8 }}>
            {images.map((img) => (
              <Paper key={img._id} withBorder radius="md" style={{ overflow: "hidden" }}>
                <Image
                  src={img.url || `/uploads/${img.path}`}
                  h={100}
                  fit="contain"
                  bg="gray.1"
                />
                <Group p={4} justify="center" gap={4}>
                  {statusFilter === "new" && (
                    <>
                      <Button
                        size="compact-xs"
                        color="green"
                        variant="light"
                        onClick={() => handleReview(img._id, "success")}
                      >
                        <IconCheck size={14} />
                      </Button>
                      <Button
                        size="compact-xs"
                        color="red"
                        variant="light"
                        onClick={() => handleReview(img._id, "failed")}
                      >
                        <IconX size={14} />
                      </Button>
                    </>
                  )}
                  {statusFilter !== "new" && (
                    <Badge
                      size="xs"
                      color={img.auditStatus === "success" ? "green" : "red"}
                    >
                      {img.auditStatus}
                    </Badge>
                  )}
                </Group>
              </Paper>
            ))}
          </SimpleGrid>

          {images.length === 0 && (
            <Text ta="center" c="dimmed" py="lg">
              Зураг олдсонгүй
            </Text>
          )}

          {totalPages > 1 && (
            <Group justify="center">
              <Pagination value={page} onChange={setPage} total={totalPages} />
            </Group>
          )}
        </>
      )}
    </Stack>
  );
}
